import { debounce } from "@fraction/shared";
import { type DocumentProps, usePDF } from "@react-pdf/renderer";
import {
  forwardRef,
  type JSXElementConstructor,
  type ReactElement,
  type ReactNode,
  useEffect,
  useImperativeHandle,
} from "react";
import Skeleton from "src/components/Skeleton";
import { Download } from "src/icons";
import { useMutation } from "src/lib";
import { downloadFile } from "src/utilities/file/download";
import { cn } from "src/utilities/shadcnUtils";
import ScrollablePDFViewer from "./ScrollablePDFViewer";

/**
 * A PDF viewer that can be used to display PDFs in the browser, including on mobile. Memoize the children
 * to prevent unnecessary re-renders.
 */
export const PDFViewer = forwardRef(
  (
    {
      children,
      filename,
      className,
      shovedClassName,
      shoved,
      download,
      onDownload,
      url,
      toolbar = "header",
      toolbarElements,
      toolbarClassName,
      shovedToolbarClassName,
      pdfClassName,
      shovedPdfClassName,
      renderTextLayer,
      onLoadError,
      initialPage,
      highlightText,
    }: {
      toolbar?: "header" | "footer";
      children?: ReactElement<DocumentProps, string | JSXElementConstructor<any>>;
      filename?: string;
      className?: string;
      shovedClassName?: string;
      shoved?: boolean;
      pdfClassName?: string;
      toolbarClassName?: string;
      shovedToolbarClassName?: string;
      shovedPdfClassName?: string;
      download?: boolean | string;
      toolbarElements?: ReactNode;
      onDownload?: () => void;
      url?: string;
      renderTextLayer?: boolean;
      onLoadError?: (error: Error) => void;
      initialPage?: number;
      highlightText?: string;
    },
    ref
  ) => {
    const [instance, updateInstance] = usePDF({ document: children });

    const debouncedUpdateInstance = debounce(updateInstance, 100);

    useEffect(() => {
      if (children) {
        debouncedUpdateInstance(children);
      }
    }, [children]);

    const downloadMutation = useMutation({
      mutationFn: async (_event: any) => {
        const downloadUrl = url || instance.url;
        if (!downloadUrl) {
          return null;
        }
        onDownload?.();
        return downloadFile(downloadUrl, `${filename}.pdf`);
      },
    });

    useImperativeHandle(ref, () => ({
      download: downloadMutation.mutateAsync,
    }));

    if (instance.loading) {
      const skeletonClassName = className?.match(/\sh-(.*?)\s/)?.[0];
      return <Skeleton width="100%" height="100%" className={skeletonClassName || undefined} />;
    }

    if (instance.error) {
      return (
        <div>
          Something went wrong: {typeof instance.error === "string" ? instance.error : "Unknown error"}
        </div>
      );
    }

    const Toolbar = () =>
      download || toolbarElements ? (
        <div
          className={cn(
            "h-12 bg-gray w-full items-center flex flex-row px-6 justify-between",
            toolbar === "header" ? "rounded-t align-top" : "rounded-b align-bottom",
            toolbarClassName,
            shoved && shovedToolbarClassName
          )}
        >
          {toolbarElements ? toolbarElements : <div />}

          {download ? (
            <button
              onClick={downloadMutation.mutate}
              className="flex flex-row items-center gap-3 text-primary-foreground text-sm"
            >
              {typeof download === "string" ? download : null}
              <Download className="text-primary-foreground" />
            </button>
          ) : null}
        </div>
      ) : null;

    return (
      <div className={cn("flex flex-col", shoved ? shovedClassName : className)}>
        {toolbar === "header" && <Toolbar />}
        <ScrollablePDFViewer
          className={cn("min-h-0 flex-1 w-full", shoved ? shovedPdfClassName : pdfClassName)}
          newTab={false}
          file={url || instance.url}
          renderTextLayer={renderTextLayer}
          onLoadError={onLoadError}
          initialPage={initialPage}
          highlightText={highlightText}
        />
        {toolbar === "footer" && <Toolbar />}
      </div>
    );
  }
);
